import moment from 'moment';
import { getApiErrorMessage } from '../utils/apiError';

import * as Api from '../api';
import { FILTER_KEYS } from '../pages/SearchPage/SearchV2/constants';
import { INITIAL_FILTERS } from '../reducers/searchReducer';
import { URL_DATE_FORMATS } from '../utils/dateFormats';
import { formatDistanceMi } from '../utils/distance';
import {
    CONFIDENCE_HIGH,
    CONFIDENCE_NEEDS_CLARIFICATION,
    NL_URL_PARAMS,
    decodeGroundedContext,
} from '../utils/naturalSearch';
import { search, updateQueryParams } from './search-actions';
import {
    NATURAL_SEARCH_FETCH_REQUEST,
    NATURAL_SEARCH_FETCH_SUCCESS,
    NATURAL_SEARCH_FETCH_FAILURE,
    NATURAL_SEARCH_NEEDS_CLARIFICATION,
    NATURAL_SEARCH_MARK_APPLIED,
    NATURAL_SEARCH_REHYDRATE,
} from './actionTypes/natural-search-action-types';
import {
    SEARCH_UPDATE_QUERY,
    SEARCH_REPLACE_FILTER_INPUTS,
    SEARCH_UPDATE_FILTERS_APPLIED,
} from './actionTypes/search-action-types';

const DATE_FILTER_KEYS = new Set([FILTER_KEYS.START_DATE, FILTER_KEYS.END_DATE]);

// LLM emits these as arrays; redux/search state stores them as boolean dicts
// (see searchApi.appendBooleanFilterEntries). The NL set is wider than
// SearchV2/constants.js FILTER_CONFIG.CHECKBOX_KEYS (which covers V2 events
// only) — it also handles player-search slices (tours, eventRegions, etc.).
// Treat this list as the source of truth for boolean-dict conversion on the
// LLM side; CHECKBOX_KEYS is a V2-UI subset and can drift if SearchV2's
// filter universe changes.
const ARRAY_TO_DICT_FILTER_KEYS = new Set([
    FILTER_KEYS.EVENT_TYPES,
    FILTER_KEYS.BALL_TYPES,
    FILTER_KEYS.GENDERS,
    FILTER_KEYS.TOURS,
    FILTER_KEYS.EVENT_REGIONS,
    FILTER_KEYS.EVENT_UTR_TYPES,
    FILTER_KEYS.EVENT_TAGS,
    FILTER_KEYS.EVENT_TEAM_TYPES,
    FILTER_KEYS.HS_GRAD_YEARS,
    FILTER_KEYS.SPORT_TYPES,
]);

// Only keys the search layer understands may be copied from the LLM response —
// this keeps arbitrary model-emitted keys (and prototype-polluting ones like
// __proto__) out of filter state.
const ALLOWED_FILTER_KEYS = new Set(Object.values(FILTER_KEYS));

export function translateLlmFilters(llmResponse, defaults = INITIAL_FILTERS) {
    const out = { ...defaults };

    if (llmResponse?.datatype) {
        out[FILTER_KEYS.TYPE] = llmResponse.datatype;
    }

    // Mirror the geocoder-resolved label into filters.location so the legacy
    // structured LocationFilter pill ("Near {place}") lights up alongside the
    // NL chip. The chip itself reads from resolvedContext.locationLabel; this
    // line is purely a bridge for the older filter UI.
    const locationLabel = llmResponse?.resolvedContext?.locationLabel;
    if (locationLabel) {
        out[FILTER_KEYS.LOCATION] = locationLabel;
    }

    const llmFilters = llmResponse?.filters ?? {};
    Object.entries(llmFilters).forEach(([key, value]) => {
        if (key === FILTER_KEYS.PIN && typeof value === 'string') {
            // "lat,lng" string → separate lat/lng number fields.
            const parts = value.split(',').map((s) => Number(s.trim()));
            if (parts.length === 2 && Number.isFinite(parts[0]) && Number.isFinite(parts[1])) {
                [out[FILTER_KEYS.LAT], out[FILTER_KEYS.LNG]] = parts;
            }
            return;
        }
        if (ARRAY_TO_DICT_FILTER_KEYS.has(key) && Array.isArray(value)) {
            out[key] = value.reduce((acc, item) => {
                if (item != null && item !== '') {
                    acc[item] = true;
                }
                return acc;
            }, {});
            return;
        }
        if (key === FILTER_KEYS.DISTANCE && typeof value === 'number') {
            // Legacy filters store distance as "Nmi"; LLM emits a number.
            out[key] = formatDistanceMi(value);
            return;
        }
        if (DATE_FILTER_KEYS.has(key) && typeof value === 'string') {
            // V2 events search calls filters.startDate.format(...) expecting a
            // moment object; LLM emits an ISO date string. URL_DATE_FORMATS
            // mirrors eventSearchHelpers' coerceUrlDate so both sites accept
            // the same shapes and avoid moment's locale-dependent fallback.
            out[key] = moment(value, URL_DATE_FORMATS);
            return;
        }
        if (ALLOWED_FILTER_KEYS.has(key)) {
            out[key] = value;
        }
    });

    return out;
}

export function rehydrateNaturalSearchFromUrl(parsed) {
    return (dispatch) => {
        if (!parsed) {
            return;
        }
        const query = parsed[NL_URL_PARAMS.QUERY];
        const confidence = parsed[NL_URL_PARAMS.CONFIDENCE] === CONFIDENCE_HIGH
            ? CONFIDENCE_HIGH
            : null;
        const resolvedContext = decodeGroundedContext(parsed[NL_URL_PARAMS.GROUNDED]);
        if (!query && !confidence && !resolvedContext) {
            return;
        }
        dispatch({
            type: NATURAL_SEARCH_REHYDRATE,
            query: query || null,
            confidence,
            resolvedContext,
        });
    };
}

export function naturalSearch(query) {
    return async (dispatch, getState, { mainApi }) => {
        if (!query?.trim()) {
            return;
        }

        dispatch({ type: NATURAL_SEARCH_FETCH_REQUEST, query });

        try {
            const result = await Api.naturalSearch(mainApi, query);
            const data = result?.data;

            if (!data) {
                dispatch({
                    type: NATURAL_SEARCH_FETCH_FAILURE,
                    error: 'Empty response from natural-language search.',
                });
                return;
            }

            if (data.confidence === CONFIDENCE_NEEDS_CLARIFICATION) {
                dispatch({ type: NATURAL_SEARCH_NEEDS_CLARIFICATION, data });
                return;
            }

            // Route filters.query through state.search.query so the URL
            // writer doesn't emit two query= params; rest-pick everything else.
            const { query: llmQuery = '', ...inferredFilters } = translateLlmFilters(data);

            dispatch({ type: NATURAL_SEARCH_FETCH_SUCCESS, data });
            dispatch({ type: SEARCH_UPDATE_QUERY, query: llmQuery });
            dispatch({ type: SEARCH_REPLACE_FILTER_INPUTS, filters: inferredFilters });
            dispatch({ type: SEARCH_UPDATE_FILTERS_APPLIED });
            // Must fire before the URL changes so SearchResults' type-change
            // wipe (clearFilters in componentDidUpdate) can detect NL origin.
            dispatch({ type: NATURAL_SEARCH_MARK_APPLIED });
            dispatch(search());
            dispatch(updateQueryParams(true));
        } catch (error) {
            const message = getApiErrorMessage(error, 'Natural-language search failed.');
            dispatch({ type: NATURAL_SEARCH_FETCH_FAILURE, error: message });
        }
    };
}
